/**
 * Validasi lokasi kerja sebelum dikirim — MURNI.
 *
 * Server tetap yang memutuskan (`@IsLatitude`, `@IsLongitude`, radius 1–50.000). Yang ada di sini
 * bukan lapisan keamanan melainkan penghematan perjalanan: memberi tahu admin apa yang kurang
 * sebelum ia menunggu jawaban server, dan menolak bentuk yang jelas salah sebelum menjadi 400
 * yang berbunyi "Validasi gagal" tanpa menyebut kolomnya.
 *
 * Diadaptasi dari aplikasi absensi-kurir Emguard, yang menolak radius di bawah 10 meter di sisi
 * klien karena tim menemukan sendiri bahwa radius kecil membuat orang gagal absen sambil berdiri
 * tepat di gudang. Batas itu dipertahankan di sini — server menerima 1 meter, tetapi tidak ada
 * geofence yang berguna sesempit itu.
 */

export interface WorkLocationDraft {
  readonly name: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly radiusMeters: string;
}

/** Batas bawah yang lebih ketat dari server — lihat alasan di kepala berkas. */
export const MIN_USEFUL_RADIUS_METERS = 10;
export const MAX_RADIUS_METERS = 50_000;

export interface DraftProblem {
  readonly field: 'name' | 'point' | 'radius';
  readonly message: string;
}

/**
 * Masalah pertama yang menghalangi penyimpanan, atau `null` bila sudah layak dikirim.
 *
 * Mengembalikan SATU masalah, bukan daftar: layar ini dipakai sambil menunjuk peta, dan daftar
 * galat panjang di layar sempit membuat orang membaca yang pertama lalu berhenti. Urutannya
 * mengikuti urutan pengisian, sehingga yang muncul selalu langkah berikutnya yang harus dikerjakan.
 */
export function validateDraft(draft: WorkLocationDraft): DraftProblem | null {
  const nama = draft.name.trim();
  if (nama.length < 2) {
    return { field: 'name', message: 'Nama lokasi minimal 2 huruf.' };
  }

  if (draft.latitude === null || draft.longitude === null) {
    return { field: 'point', message: 'Ketuk peta untuk menandai titik lokasi.' };
  }
  if (draft.latitude < -90 || draft.latitude > 90) {
    return { field: 'point', message: 'Lintang di luar rentang yang sah.' };
  }
  if (draft.longitude < -180 || draft.longitude > 180) {
    return { field: 'point', message: 'Bujur di luar rentang yang sah.' };
  }

  const radius = Number(draft.radiusMeters);
  if (!Number.isInteger(radius)) {
    return { field: 'radius', message: 'Radius harus bilangan bulat dalam meter.' };
  }
  if (radius < MIN_USEFUL_RADIUS_METERS) {
    return {
      field: 'radius',
      message: `Radius minimal ${MIN_USEFUL_RADIUS_METERS} meter — lebih sempit dari itu membuat orang gagal absen meski berdiri di lokasi.`,
    };
  }
  if (radius > MAX_RADIUS_METERS) {
    return { field: 'radius', message: `Radius maksimal ${MAX_RADIUS_METERS} meter.` };
  }

  return null;
}

/**
 * Koordinat untuk ditampilkan. Enam desimal ≈ 0,11 meter — cukup untuk geofence, dan lebih dari
 * itu hanya memanjangkan angka tanpa menambah ketelitian yang berarti bagi GPS ponsel.
 */
export function formatCoordinate(latitude: number, longitude: number): string {
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

export interface PlaceSuggestion {
  readonly label: string;
  readonly latitude: number;
  readonly longitude: number;
}

/**
 * Membaca hasil pencarian tempat Nominatim (OpenStreetMap).
 *
 * Bentuknya milik layanan luar, jadi tidak dipercaya: baris tanpa koordinat yang dapat diurai
 * DIBUANG, bukan dibiarkan menjadi `NaN` yang lolos ke peta dan memindahkan penanda ke Null
 * Island (0, 0) — kesalahan yang terlihat seperti bug peta, bukan seperti data buruk.
 */
export function parsePlaceSuggestions(payload: unknown): readonly PlaceSuggestion[] {
  if (!Array.isArray(payload)) return [];
  return payload.flatMap((row): PlaceSuggestion[] => {
    if (typeof row !== 'object' || row === null) return [];
    const record = row as Record<string, unknown>;
    const latitude = Number(record.lat);
    const longitude = Number(record.lon);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];
    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return [];
    const label = typeof record.display_name === 'string' ? record.display_name : '';
    if (label.length === 0) return [];
    return [{ label, latitude, longitude }];
  });
}

/**
 * Peringatan saat titik diambil dari GPS — MURNI.
 *
 * Menekan "pakai lokasi saya" terasa seperti jawaban yang pasti, padahal perangkat hanya
 * melaporkan "saya berada di suatu tempat dalam lingkaran seluas ini". Bila lingkaran itu
 * sebanding dengan radius geofence-nya, titik pusat yang tersimpan bisa meleset sejauh radius
 * itu sendiri — dan geofence yang dikira 150 m sebenarnya berperilaku seperti 230 m ke satu arah
 * dan 70 m ke arah lain.
 *
 * Akibatnya tidak terlihat saat menyimpan. Ia muncul berminggu-minggu kemudian sebagai absen yang
 * "kadang ditolak padahal orangnya di gudang".
 */
export function gpsAccuracyWarning(
  accuracyMeters: number | null,
  radiusMeters: number,
): string | null {
  if (accuracyMeters === null || !Number.isFinite(accuracyMeters)) return null;
  if (accuracyMeters <= 0) return null;
  // Sepertiga radius: di bawah itu, meleset sejauh akurasi masih tertutup radius dengan sisa yang
  // memadai. Angkanya penilaian, bukan hukum — karena itu ia peringatan, bukan penolakan.
  if (accuracyMeters * 3 <= radiusMeters) return null;
  return (
    `Pembacaan GPS hanya akurat ±${Math.round(accuracyMeters)} m, sementara radiusnya ` +
    `${Math.round(radiusMeters)} m. Titiknya bisa meleset sejauh itu. Berdiri di area terbuka ` +
    `lalu baca ulang, atau perbesar radiusnya.`
  );
}
